import * as Sentry from '@sentry/react';
import _ from 'lodash';

import { localStorageService } from 'services/localStorage';

import { getPackageVersion, getTenant } from 'shared/functions-ts';

import { OrigContextType, SentryInitType } from './types';

export const sentry = {
  init: (options: SentryInitType = {}) => {
    if (!process.env.TRACING_URL) {
      return;
    }

    if (localStorageService.disableTracing) {
      return;
    }

    const {
      autoSessionTracking = true,
      tracesSampleRate = 1.0,
      replaysSessionSampleRate = 1.0,
      replaysOnErrorSampleRate = 1.0,
      profilesSampleRate = 1.0,
      normalizeDepth = 4,
    } = options;

    Sentry.init({
      dsn: process.env.TRACING_URL,
      debug: !!process.env.TRACING_DEBUG,
      environment: process.env.TRACING_ENVIRONMENT,
      release: getPackageVersion(),

      autoSessionTracking: autoSessionTracking,
      tracesSampleRate: tracesSampleRate,
      replaysSessionSampleRate: replaysSessionSampleRate,
      replaysOnErrorSampleRate: replaysOnErrorSampleRate,
      profilesSampleRate: profilesSampleRate,

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          networkDetailAllowUrls: ['localhost', /https:\/\/(dev*|uat*).api\.eva\.ai\/api/],
          networkRequestHeaders: [],
          networkResponseHeaders: [],
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracePropagationTargets: ['localhost', /https:\/\/.*\.api\.eva\.ai\/api/],
      normalizeDepth: normalizeDepth,
      beforeSend: (event) => {
        const user = localStorageService.user;
        if (user) {
          event.user = {
            ...event.user,
            id: user.userId,
          };
        }

        event.tags = {
          ...event.tags,
          tenant: getTenant(),
        };

        return event;
      },
    });
  },
  captureException: (err: Error | string, origContext: OrigContextType = {}) => {
    if (!process.env.TRACING_URL) {
      return;
    }

    const error: Error = typeof err === 'string' ? new Error(err) : err;

    const context = origContext || {};
    const { errorName = 'UnknownError', severity = 'error' } = context;

    Sentry.withScope(function (scope) {
      const filtredContext = _.omit(origContext, ['errorName', 'severity']);

      const fingerprint = errorName || error.name;

      if (fingerprint) {
        scope.setFingerprint([fingerprint]);
      }

      if (severity === 'info') {
        scope.setLevel('info');
      }

      if (severity === 'warning' || fingerprint === 'NetworkError') {
        scope.setLevel('warning');
      }

      scope.setContext('context', filtredContext);
      Sentry.captureException(error);
    });
  },
};
